import { DropOptions, NodeModel, Tree } from '@minoru/react-dnd-treeview'
import React, { useMemo } from 'react'
import { TreeGroupNode } from '../tree-group-node'
import { TreeGroupUserNode } from '../tree-group-user-node'
import { UnifiedGroupTreeNode } from '@/entities/groups'
import { RoleType } from '@/shared/api/users'
import { addUser, updateGroup } from '@/shared/api/groups'
import { useAppDispatch } from '@/shared/lib/redux'
import { addUserToGroup, removeUserFromGroup, updateGroupParent } from '@/entities/groups'

interface GroupTreeProps {
    treeData: NodeModel<UnifiedGroupTreeNode>[]
    rootId: number | string
    role: RoleType
    onUpdate: () => void
    defaultGroupId?: string | null
    onSetDefaultGroup?: (groupId: string) => void | Promise<void>
}

export const GroupTree: React.FC<GroupTreeProps> = ({ treeData, onUpdate, role, rootId, defaultGroupId, onSetDefaultGroup }) => {
    const dispatch = useAppDispatch()

    const treeKey = useMemo(
        () => treeData.map((node) => `${node.id}:${node.parent}`).join('|'),
        [treeData]
    )

    const onDrop = async (tree: NodeModel<UnifiedGroupTreeNode>[], options: DropOptions<UnifiedGroupTreeNode>) => {
        if (!options.dropTarget || options.dropTarget.data.type === 'user') {
            return
        }
        if (options.dragSource.data.type === 'user') {
            const userId = options.dragSource.data.id
            const newGroupId = options.dropTarget.data.id
            const oldGroupId = options.dragSource.data.parent
            const username = options.dragSource.data.username

            try {
                await addUser(newGroupId, userId)
                // Remove from old group if user was in one
                if (oldGroupId) {
                    dispatch(removeUserFromGroup({ groupId: oldGroupId, userId, role }))
                }
                // Add to new group
                dispatch(addUserToGroup({ 
                    groupId: newGroupId, 
                    user: { id: userId, username }, 
                    role 
                }))
            } catch (error) {
                onUpdate()
            }
        }
        if (options.dragSource.data.type === 'group') {
            const groupId = options.dragSource.data.id
            const newParentId = options.dropTarget?.data?.id ?? null

            try {
                const response = await updateGroup({
                    group_id: groupId,
                    parent_id: newParentId
                })
                dispatch(updateGroupParent({ 
                    groupId, 
                    parentId: response.data.parent_id, 
                    role 
                }))
            } catch (error) {
                onUpdate()
            }
        }
    }

    return (
        <div className="p-3 rounded-2xl bg-white">
            <Tree
                key={treeKey}
                tree={treeData}
                rootId={rootId}
                classes={{
                    container: '[&_ul]:max-h-[360px] [&_ul]:overflow-auto'
                }}
                sort={false}
                render={(node, options) => (
                    node.data.type === 'group'
                        ? <TreeGroupNode 
                            node={node as any}
                            onToggle={options.onToggle}
                            isOpen={options.isOpen}
                            depth={options.depth} 
                            role={role}
                            defaultGroupId={defaultGroupId}
                            onSetDefaultGroup={onSetDefaultGroup}
                        />
                        : <TreeGroupUserNode 
                            node={node as any} 
                            depth={options.depth}                              
                        />
                )}
                onDrop={onDrop}
            />
        </div>
    )
}